import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Carousel from '../../components/Carousel';
import ScrollAnimate from '../../components/ScrollAnimate';
import './LandingPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface CarouselItem {
  id: string;
  title: string;
  body: string;
  design: 'primary' | 'secondary' | 'success' | 'info' | 'light';
  order: number;
  enabled: boolean;
}

const LandingPage: React.FC = () => {
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [carouselInterval, setCarouselInterval] = useState(6000);

  useEffect(() => {
    fetchCarouselSettings();
  }, []);

  const fetchCarouselSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/settings`);
      if (response.data.carouselItems) {
        setCarouselItems(response.data.carouselItems);
      }
      if (response.data.carouselAutoPlayInterval) {
        setCarouselInterval(response.data.carouselAutoPlayInterval);
      }
    } catch (error) {
      console.error('Failed to fetch carousel settings:', error);
    }
  };

  return (
    <div className="landing-page">
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Hva er Sykkelfest?</h1>
            <p className="hero-subtitle">
              Sykkelfest er en sosial og aktiv matopplevelse der du blir kjent med nye mennesker – én rett av gangen.
            </p>
            <div className="hero-actions">
              <Link to="/bli-med" className="btn btn-primary btn-large">
                Bli med på fest
              </Link>
              <Link to="/opprett-konto" className="btn btn-primary btn-large">
                Opprett konto
              </Link>
            </div>
          </div>
          <div className="hero-image-container">
            <img src="/sykkelfest-hero.png" alt="Sykkelfest" className="hero-main-image" />
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      {carouselItems.length > 0 && (
        <section className="carousel-section">
          <Carousel items={carouselItems} autoPlayInterval={carouselInterval} />
        </section>
      )}

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <p>
            Du melder deg på sammen med en partner, og i løpet av kvelden sykler dere mellom ulike hjem og steder for å spise forrett, hovedrett og dessert. Hvert stopp deles med nye par, slik at dere møter forskjellige mennesker underveis. Til slutt samles alle deltakerne på ett felles sted for en hyggelig avslutning og etterfest.
          </p>
          <p>
            Sykkelfest kombinerer matglede, bevegelse og sosialt fellesskap – og passer perfekt for deg som ønsker en annerledes og minnerik kveld.
          </p>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section image-section">
          <img 
            src="/sykkelfest-environment.png" 
            alt="Sykkelfest arrangement" 
            className="hero-image"
          />
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <h2>Hvordan fungerer det?</h2>

          <div className="feature-grid">
            <ScrollAnimate animation="fade-in" delay={0.1}>
              <div className="feature-card">
                <h3>Meld deg på som par</h3>
                <p>Du deltar alltid sammen med én partner. Sammen utgjør dere ett par gjennom hele arrangementet.</p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate animation="fade-in" delay={0.2}>
              <div className="feature-card">
                <h3>Alle par lager én rett</h3>
                <p>Hvert par er vertskap for én av rettene: forrett, hovedrett eller dessert. Resten av måltidene spiser dere hos andre.</p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate animation="fade-in" delay={0.3}>
              <div className="feature-card">
                <h3>Tre stopp – tre nye møter</h3>
                <p>I løpet av kvelden besøker dere tre forskjellige steder. På hvert stopp møter dere to nye par. Systemet sørger for at dere ikke møter de samme menneskene flere ganger.</p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate animation="fade-in" delay={0.4}>
              <div className="feature-card">
                <h3>Sykle mellom stoppene</h3>
                <p>Dere sykler fra sted til sted. Rutene er planlagt slik at avstandene er overkommelige og tilpasset området.</p>
              </div>
            </ScrollAnimate>

            <ScrollAnimate animation="fade-in" delay={0.5}>
              <div className="feature-card">
                <h3>Etterfest for alle</h3>
                <p>Når alle måltider er gjennomført, samles alle deltakerne på ett felles sted for sosial avslutning og fest.</p>
              </div>
            </ScrollAnimate>
          </div>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section image-section">
          <img 
            src="/sykkelfest-4part.png" 
            alt="Sykkelfest - fire bilder fra arrangementet" 
            className="hero-image"
          />
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <h2>Underveis på kvelden</h2>
          <ul className="feature-list">
            <li>Dere får kun informasjon om neste stopp, slik at opplevelsen forblir spennende</li>
            <li>Dere får beskjed når det er tid for å dra videre</li>
            <li>Adresse og kartlenke vises direkte på mobilen</li>
          </ul>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <h2>Etter Sykkelfesten</h2>
          <p>Når kvelden er over, kan dere:</p>
          <ul className="feature-list">
            <li>Se hvilke par dere har delt måltider med</li>
            <li>Få oversikt over kveldens rute og stopp</li>
            <li>Gi tilbakemelding om opplevelsen</li>
          </ul>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section image-section">
          <img 
            src="/sykkelfest-message.png" 
            alt="Melding om neste stopp på Sykkelfest" 
            className="hero-image"
          />
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <h2>Typisk tidsplan</h2>
          <p>
            Her er et eksempel på hvordan en Sykkelfest-kveld kan se ut når arrangementet starter klokken 16:00:
          </p>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <div className="timeline-example">
            <div className="timeline-item timeline-message">
              <div className="timeline-time">15:30</div>
              <div className="timeline-content">
                <strong>Informasjon om første stopp</strong>
                <p>Du får informasjon om hvor forrett serveres. På tide å komme seg på hjul</p>
              </div>
            </div>
            <div className="timeline-item timeline-meal timeline-starter">
              <div className="timeline-time">16:00</div>
              <div className="timeline-content">
                <strong>Forrett serveres</strong>
                <p>Møt dine første verter og nyt forretten sammen med to andre par</p>
              </div>
            </div>
            <div className="timeline-item timeline-message">
              <div className="timeline-time">17:00</div>
              <div className="timeline-content">
                <strong>Informasjon om hovedrett</strong>
                <p>Informasjon om hvor hovedrett serveres. Sykle videre til neste stopp</p>
              </div>
            </div>
            <div className="timeline-item timeline-meal timeline-main">
              <div className="timeline-time">17:30</div>
              <div className="timeline-content">
                <strong>Hovedrett serveres</strong>
                <p>Møt nye mennesker og nyt hovedretten i godt lag</p>
              </div>
            </div>
            <div className="timeline-item timeline-message">
              <div className="timeline-time">19:00</div>
              <div className="timeline-content">
                <strong>Informasjon om dessert</strong>
                <p>Nå slippes detaljer om hvor dessert serveres - på med sykkelhjelmen igjen</p>
              </div>
            </div>
            <div className="timeline-item timeline-meal timeline-dessert">
              <div className="timeline-time">19:15</div>
              <div className="timeline-content">
                <strong>Dessert serveres</strong>
                <p>Møt enda flere glade venner til en søt avslutning før kveldens felles fest</p>
              </div>
            </div>
            <div className="timeline-item timeline-party">
              <div className="timeline-time">20:15</div>
              <div className="timeline-content">
                <strong>Etterfest på sluttdestinasjon!</strong>
                <p>Alle samles på ett felles sted for en hyggelig avslutning og sosialt samvær - del historier fra kvelden og kos dere videre med alle som har vært med på sykkelfesten</p>
              </div>
            </div>
          </div>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section image-section">
          <img 
            src="/happy-couple.png" 
            alt="Lykkelig par som lager mat, sykler, spiser og feirer sammen" 
          className="feature-image"
        />
      </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section">
          <h2>Hvem passer Sykkelfest for?</h2>
          <p>Sykkelfest passer for:</p>
          <ul className="feature-list">
            <li>Venner</li>
            <li>Kjærester</li>
            <li>Kollegaer</li>
            <li>Borettslag</li>
            <li>Nye innbyggere</li>
            <li>Alle som ønsker å møte nye mennesker på en uformell måte</li>
          </ul>
          <p className="highlight">
            Du trenger ikke å kjenne noen på forhånd – Sykkelfest er laget for å gjøre det enkelt å bli kjent.
          </p>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section summary">
          <h2>Kort oppsummert</h2>
          <div className="summary-items">
            <div className="summary-item">✓ Sosial matopplevelse</div>
            <div className="summary-item">✓ Aktiv og miljøvennlig</div>
            <div className="summary-item">✓ Nye mennesker ved hvert stopp</div>
            <div className="summary-item">✓ Strukturert, men uformelt</div>
            <div className="summary-item">✓ En kveld du garantert vil huske</div>
            <div className="summary-item">✓ Matinspirasjoner du kan ta med til hverdagen</div>
          </div>
        </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="cta-section">
        <h2>Klar for en uforglemmelig kveld?</h2>
        <p className="hero-subtitle">
          Bli en sykkelfester du også! Sykkelfest er en sosial og aktiv matopplevelse der du blir kjent med nye mennesker – én rett av gangen.
        </p>
        <div className="cta-actions">
          <Link to="/opprett-konto" className="btn btn-primary btn-large">
            Meld deg på
          </Link>
          <Link to="/arrangor/opprett" className="btn btn-primary btn-large">
            Opprett sykkelfest
          </Link>
        </div>
      </section>
      </ScrollAnimate>

      <ScrollAnimate animation="fade-in">
        <section className="content-section image-section">
          <img 
            src="/sykkelfest-top-banner.png" 
            alt="Sykkelfest banner" 
            className="feature-image"
          />
        </section>
      </ScrollAnimate>
    </div>
  );
};

export default LandingPage;
